import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    IconButton,
    Alert,
    CircularProgress,
    FormControl,
    Select,
    MenuItem,
    Chip,
    Card,
    CardContent,
    Tooltip,
    Stack,
    InputAdornment,
    Collapse,
    ButtonBase,
} from '@mui/material';
import {
    Add,
    Search as SearchIcon,
    Clear as ClearIcon,
    ExpandMore,
    ExpandLess,
    History as HistoryIcon,
} from '@mui/icons-material';
import { businessAPI, notificationAPI, planSignupAPI } from '../api';
import { STATUS_TAGS, statusTagColor, buildStatusTagMap } from '../utils/customerTags';
import { sortAreasByRoute } from '../utils/routeOrder';
import AreaFilterRow from './common/AreaFilterRow';
import CustomerPlans, { buildCustomerPlanState } from './CustomerPlans';
import ScrollChipRow from './common/ScrollChipRow';

// Area filter value for "customers in no area" — not a real area id.
const NO_AREA_FILTER = 'none';

const NOTIF_PAGE_SIZE = 50;

// The same stored status means different things per type: a plan change is only ever
// acknowledged ("Got it" stores 'rejected'), while a sign-up request is genuinely
// approved or declined. Label them accordingly so the log reads truthfully.
const notifOutcome = (n) => {
    if (n.f_type === 'plan_signup') {
        if (n.f_status === 'accepted') return { label: 'Approved', color: 'success' };
        if (n.f_status === 'rejected') return { label: 'Declined', color: 'error' };
        return { label: 'Waiting', color: 'warning' };
    }
    // subscription_change — informational
    return n.f_status === 'pending'
        ? { label: 'New', color: 'warning' }
        : { label: 'Seen', color: 'default' };
};

// ── Customer card: name, status and +, then the shared CustomerPlans ──────
// `expanded` is owned by the parent so only one card's history is open at a time.
const CustomerPlanCard = React.memo(function CustomerPlanCard({
    businessId, customer, logs, statusTag, expanded, onToggleExpand, products, onChanged,
}) {
    const plansRef = useRef(null);
    return (
        <Card
            variant="outlined"
            sx={{
                transition: 'border-color 0.2s, box-shadow 0.2s',
                ...(expanded && {
                    borderColor: 'primary.main',
                    boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
                }),
            }}
        >
            <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                            <Typography variant="body2" fontWeight="bold" noWrap>
                                {customer.f_name}
                            </Typography>
                            {statusTag && (
                                <Chip label={statusTag} size="small" color={statusTagColor(statusTag)}
                                    sx={{ height: 18, fontSize: '0.65rem' }} />
                            )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            {customer.f_mobile}
                        </Typography>
                    </Box>
                    <Tooltip title="Add plan">
                        <IconButton size="small" color="primary" onClick={() => plansRef.current?.openAdd()}>
                            <Add fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <CustomerPlans ref={plansRef} businessId={businessId} customer={customer} logs={logs}
                    products={products} onChanged={onChanged} expanded={expanded} onToggleExpand={onToggleExpand} />
            </CardContent>
        </Card>
    );
});

const CustomerSubscriptionManager = ({ businessId }) => {
    const [customers, setCustomers]     = useState([]);
    const [products, setProducts]       = useState([]);
    const [allLogs, setAllLogs]         = useState([]);
    const [customerTags, setCustomerTags] = useState([]);

    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [success, setSuccess]   = useState('');

    // ── Informational notices: changes customers made to their own plans ──────
    const [planNotifs, setPlanNotifs] = useState([]);
    // ── Notification log (collapsed reference of what came in / what was done) ──
    const [historyOpen, setHistoryOpen] = useState(false);
    const [notifHistory, setNotifHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [hasMoreHistory, setHasMoreHistory] = useState(false);
    const didAutoExpand = React.useRef(false);
    // ── Sign-up requests: customers asking to start a NEW plan (approve/decline) ──
    const [signupRequests, setSignupRequests] = useState([]);
    const [approveTarget, setApproveTarget] = useState(null);
    const [approveForm, setApproveForm] = useState({ date: '' });

    // ── Search & filter ──────────────────────────────────────────────────────
    const [searchQuery, setSearchQuery]   = useState('');
    const [tagFilter, setTagFilter]       = useState({ group: '', value: '' });
    // Area combines with the status chips (Active customers in ARK B), unlike Customers, where
    // one chip at a time is the rule.
    const [areaFilter, setAreaFilter]     = useState('');
    const [areas, setAreas]               = useState([]);

    const [sortBy, setSortBy]             = useState('updated_desc');
    const [midMonthFilter, setMidMonthFilter] = useState(null); // null | 'start' | 'end'

    // ── Pagination (show 50 cards at a time, like Customers/Bills) ─────────────
    const [visibleCount, setVisibleCount] = useState(50);

    const [expandedCustomerId, setExpandedCustomerId] = useState(null);

    useEffect(() => {
        if (businessId) { loadData(); loadPlanNotifs(); loadSignupRequests(); loadNotifHistory(0); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId]);

    // Restart pagination whenever the visible set changes
    useEffect(() => { setVisibleCount(50); }, [searchQuery, tagFilter, areaFilter, sortBy, midMonthFilter]);

    const loadPlanNotifs = useCallback(async () => {
        try {
            const res = await notificationAPI.list(businessId, { type: 'subscription_change', status: 'pending' });
            const rows = res.data || [];
            setPlanNotifs(rows);
            // Open the card only when something new is waiting. Decided once, so a
            // manual collapse isn't undone by a later refresh.
            if (!didAutoExpand.current) {
                didAutoExpand.current = true;
                setHistoryOpen(rows.length > 0);
            }
        } catch { setPlanNotifs([]); }
    }, [businessId]);

    const dismissPlanNotif = async (id) => {
        try {
            await notificationAPI.reject(businessId, id);
            setPlanNotifs(list => list.filter(n => n.f_id !== id));
            loadNotifHistory(0); // dismissed row moves into the log
        } catch (e) { setError(formatError(e)); }
    };

    // ── Notification log: what came in and what happened to it ──────────────
    // Plan-related types only (payment claims have their own history in Dues).
    // Offset is passed explicitly so this stays a stable callback.
    const loadNotifHistory = useCallback(async (offset = 0) => {
        setHistoryLoading(true);
        try {
            // Resolved only — anything still pending is rendered in the "New" section
            // above from planNotifs (or, for sign-ups, its own approve/decline card).
            const res = await notificationAPI.list(businessId, {
                type: 'subscription_change,plan_signup',
                status: 'resolved',
                limit: NOTIF_PAGE_SIZE,
                offset,
            });
            const rows = res.data || [];
            setNotifHistory(prev => (offset === 0 ? rows : [...prev, ...rows]));
            setHasMoreHistory(rows.length === NOTIF_PAGE_SIZE);
        } catch { /* non-critical */ } finally {
            setHistoryLoading(false);
        }
    }, [businessId]);

    const loadSignupRequests = useCallback(async () => {
        try {
            const res = await planSignupAPI.list(businessId, { status: 'pending' });
            setSignupRequests(res.data || []);
        } catch { setSignupRequests([]); }
    }, [businessId]);

    const openApprove = (req) => {
        // Prefill with the customer's requested start date; if that has already passed, fall back
        // to tomorrow so the vendor doesn't accidentally backdate the plans.
        const d = new Date();
        d.setDate(d.getDate() + 1);
        const tomorrow = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const requested = req.requested_date;
        setApproveForm({ date: requested && requested >= tomorrow ? requested : tomorrow });
        setApproveTarget(req);
    };

    // Approving starts every plan in the request together, on one date.
    const submitApprove = async () => {
        if (!approveTarget) return;
        try {
            const res = await planSignupAPI.approve(businessId, approveTarget.id, {
                f_effective_from_date: approveForm.date,
            });
            const n = res.data?.log_ids?.length || 0;
            setSuccess(`${n} plan${n === 1 ? '' : 's'} started for ${approveTarget.customer_name || 'customer'}`);
            setApproveTarget(null);
            await Promise.all([loadSignupRequests(), loadData()]);
        } catch (e) { setError(formatError(e)); }
    };

    const declineSignup = async (req) => {
        try {
            const res = await planSignupAPI.decline(businessId, req.id);
            setSignupRequests(list => list.filter(r => r.id !== req.id));
            if (res.data?.removed_customer) setSuccess('Request declined and the new customer record was removed.');
        } catch (e) { setError(formatError(e)); }
    };

    const loadData = useCallback(async ({ quiet = false } = {}) => {
        if (!quiet) setLoading(true);
        setError('');
        try {
            const [customersRes, productsRes, logsRes, tagsRes, areasRes] = await Promise.all([
                businessAPI.getCustomers(businessId, false),
                // Plans included: a customer can be put on "Sunday Curd" as easily as on "Curd",
                // and existing plan subscriptions need their name to resolve here. Deleted ones
                // too, so an ended plan on a deleted product still shows its name.
                businessAPI.getBusinessProducts(businessId, true, true),
                businessAPI.getCustomerSubscriptions(businessId, null, null, null, false),
                businessAPI.getCustomerTags(businessId),
                businessAPI.getBusinessAreas(businessId).catch(() => ({ data: [] })),
            ]);
            setAreas(areasRes.data || []);
            setCustomers(customersRes.data || []);
            setProducts(productsRes.data || []);
            setAllLogs(logsRes.data || []);
            setCustomerTags(tagsRes.data || []);
        } catch (err) {
            setError(formatError(err));
        } finally {
            setLoading(false);
        }
    }, [businessId]);

    const formatError = (err) => {
        if (Array.isArray(err.response?.data?.detail))
            return err.response.data.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        return err.response?.data?.detail || err.message || 'An error occurred';
    };

    // ── Tag maps ─────────────────────────────────────────────────────────────
    const statusTagMap = useMemo(() => buildStatusTagMap(customerTags), [customerTags]);

    // ── Computed: per-customer active subscriptions + full history ───────────
    const customerGroups = useMemo(() => {
        const logsByCust = {};
        allLogs.forEach(log => { (logsByCust[log.f_business_customer_id] ||= []).push(log); });
        return customers.map(c => {
            const logs = logsByCust[c.f_id] || [];
            return { customer: c, logs, ...buildCustomerPlanState(logs) };
        });
    }, [allLogs, customers]);

    // ── Mid-month customer sets (last 2 months) ──────────────────────────────
    const midMonthCustomerIds = useMemo(() => {
        const now = new Date();
        const cutoff = new Date(now.getFullYear(), now.getMonth() - 1, 1)
            .toISOString().split('T')[0];

        const startIds = new Set();
        const endIds = new Set();

        allLogs.forEach(log => {
            const date = log.f_effective_from_date;
            if (!date || date < cutoff) return;
            const day = parseInt(date.split('-')[2], 10);
            if (day === 1) return;

            if (['Start', 'Resume'].includes(log.f_action)) startIds.add(log.f_business_customer_id);
            if (['Suspend', 'End'].includes(log.f_action))  endIds.add(log.f_business_customer_id);
        });

        return { startIds, endIds };
    }, [allLogs]);

    // ── Filtered + sorted customer list ─────────────────────────────────────
    const filteredGroups = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        const filtered = customerGroups
            .filter(({ customer }) => {
                if (tagFilter.group === 'Status') return statusTagMap[customer.f_id] === tagFilter.value;
                return true;
            })
            .filter(({ customer }) => {
                if (!areaFilter) return true;
                if (areaFilter === NO_AREA_FILTER) return !customer.f_area_id;
                return String(customer.f_area_id) === areaFilter;
            })
            .filter(({ customer }) => {
                if (midMonthFilter === 'start') return midMonthCustomerIds.startIds.has(customer.f_id);
                if (midMonthFilter === 'end')   return midMonthCustomerIds.endIds.has(customer.f_id);
                return true;
            })
            .filter(({ customer }) => {
                if (!q) return true;
                return [customer.f_name, customer.f_mobile]
                    .some(v => v && v.toLowerCase().includes(q));
            });
        if (sortBy === 'name') {
            filtered.sort((a, b) => (a.customer.f_name || '').localeCompare(b.customer.f_name || ''));
        } else if (sortBy === 'updated_desc' || sortBy === 'updated_asc') {
            filtered.sort((a, b) => {
                const aDate = a.history[0]?.f_updated_at || a.history[0]?.f_effective_from_date || '';
                const bDate = b.history[0]?.f_updated_at || b.history[0]?.f_effective_from_date || '';
                return sortBy === 'updated_desc'
                    ? bDate.localeCompare(aDate)
                    : aDate.localeCompare(bDate);
            });
        }
        return filtered;
    }, [customerGroups, searchQuery, tagFilter, areaFilter, statusTagMap, sortBy, midMonthFilter, midMonthCustomerIds]);

    // ── Tag counts ───────────────────────────────────────────────────────────
    const tagCounts = useMemo(() => {
        const counts = {};
        STATUS_TAGS.forEach(t => { counts[t] = 0; });
        customers.forEach(c => {
            const tag = statusTagMap[c.f_id];
            if (tag && counts[tag] !== undefined) counts[tag]++;
        });
        return counts;
    }, [customers, statusTagMap]);



    // Only one plan card's history is open at a time (accordion). Stable ref so
    // React.memo still short-circuits the cards whose expanded state didn't change.
    const toggleCardExpandCb = useCallback((customerId) => {
        setExpandedCustomerId(prev => (prev === customerId ? null : customerId));
    }, []);

    // After a plan change: reload quietly so the list stays where it is (stable ref for React.memo)
    const reloadQuietCb = useCallback(() => loadData({ quiet: true }), [loadData]);

    return (
        <Box>
            {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

            {/* ── Notifications — new items to acknowledge, then the log of what
                came in and what was done with it. Opens itself only when something
                new is waiting; the list is height-capped so it never pushes the
                plan list off a phone screen. */}
            {/* Highlight uses the same weight as an expanded plan card: 1px border +
                1px ring of the same colour (box-shadow so nothing reflows) — amber
                here rather than blue, matching the app's "needs attention" colour. */}
            <Card variant="outlined" sx={{ mb: 1.5, borderRadius: 2,
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    ...(planNotifs.length > 0 && {
                        borderColor: 'warning.main',
                        boxShadow: (theme) => `0 0 0 1px ${theme.palette.warning.main}`,
                    }) }}>
                <ButtonBase onClick={() => setHistoryOpen(o => !o)}
                    sx={{ width: '100%', justifyContent: 'flex-start', px: 1.5, py: 1.25, textAlign: 'left',
                          bgcolor: planNotifs.length > 0 ? 'warning.lighter' : 'transparent' }}>
                    <HistoryIcon fontSize="small" color={planNotifs.length > 0 ? 'warning' : 'action'} sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" fontWeight={800} sx={{ flex: 1 }}>
                        Notifications
                    </Typography>
                    {planNotifs.length > 0 ? (
                        <Chip label={`${planNotifs.length} new`} size="small" color="warning"
                            sx={{ height: 20, mr: 0.5, fontSize: '0.65rem', fontWeight: 700 }} />
                    ) : notifHistory.length > 0 && (
                        <Chip label={notifHistory.length + (hasMoreHistory ? '+' : '')} size="small"
                            sx={{ height: 20, mr: 0.5, fontSize: '0.65rem' }} />
                    )}
                    {historyOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </ButtonBase>
                <Collapse in={historyOpen} unmountOnExit>
                    <Box sx={{ px: 1.5, pb: 1.5 }}>
                        {/* New — changes customers made themselves, awaiting acknowledgement */}
                        {planNotifs.length > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                                <Typography variant="caption" color="warning.dark" fontWeight={700}
                                    sx={{ display: 'block', mb: 0.5 }}>
                                    New — changes customers made
                                </Typography>
                                <Stack spacing={0.5}>
                                    {planNotifs.map(n => (
                                        <Box key={n.f_id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                                                <strong>{n.customer_name || 'A customer'}</strong> {n.f_message}
                                            </Typography>
                                            <Button size="small" variant="text" onClick={() => dismissPlanNotif(n.f_id)}
                                                sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0, flexShrink: 0 }}>
                                                Got it
                                            </Button>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {planNotifs.length > 0 ? 'Earlier' : 'Plan changes and sign-up requests, newest first.'}
                        </Typography>

                        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            {notifHistory.map(n => {
                                const outcome = notifOutcome(n);
                                return (
                                    <Box key={n.f_id}
                                        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.75,
                                              borderTop: '1px solid', borderColor: 'divider' }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2">
                                                <strong>{n.customer_name || 'A customer'}</strong> {n.f_message}
                                            </Typography>
                                            <Typography variant="caption" color="text.disabled">
                                                {new Date(n.f_inserted_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                                            </Typography>
                                        </Box>
                                        <Chip label={outcome.label} size="small" color={outcome.color}
                                            variant={outcome.color === 'default' ? 'outlined' : 'filled'}
                                            sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, flexShrink: 0 }} />
                                    </Box>
                                );
                            })}

                            {hasMoreHistory && !historyLoading && (
                                <Box sx={{ textAlign: 'center', mt: 1 }}>
                                    <Button variant="outlined" size="small"
                                        onClick={() => loadNotifHistory(notifHistory.length)}
                                        sx={{ textTransform: 'none' }}>
                                        Load more
                                    </Button>
                                </Box>
                            )}
                        </Box>

                        {historyLoading && (
                            <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={22} /></Box>
                        )}
                        {!historyLoading && notifHistory.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: planNotifs.length > 0 ? 1.5 : 3 }}>
                                {planNotifs.length > 0 ? 'Nothing earlier.' : 'No notifications yet.'}
                            </Typography>
                        )}
                    </Box>
                </Collapse>
            </Card>

            {/* ── Plan sign-up requests (approve to create the plan) ───────── */}
            {signupRequests.length > 0 && (
                <Card variant="outlined" sx={{ mb: 1.5, borderRadius: 2, borderColor: 'primary.light', bgcolor: 'action.hover' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Add fontSize="small" color="primary" />
                            <Typography variant="subtitle2" fontWeight={800}>Plan sign-up requests</Typography>
                            <Chip label={signupRequests.length} size="small" color="primary" sx={{ height: 20, ml: 'auto' }} />
                        </Box>
                        <Stack spacing={1}>
                            {signupRequests.map(req => (
                                <Box key={req.id} sx={{ p: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                        <Typography variant="body2" fontWeight={700}>{req.customer_name || 'Customer'}</Typography>
                                        {req.is_new_customer && <Chip label="New customer" size="small" color="success" sx={{ height: 18, fontSize: '0.6rem' }} />}
                                        <Chip label={req.source === 'public' ? 'Store' : 'App'} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                                        {req.customer_mobile && <Typography variant="caption" color="text.secondary">{req.customer_mobile}</Typography>}
                                    </Box>
                                    {(req.items || []).map(it => (
                                        <Typography key={it.product_id} variant="body2" sx={{ mt: 0.25 }}>
                                            • {it.product_name} · Qty {it.quantity}
                                        </Typography>
                                    ))}
                                    {req.requested_date && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                            wants to start {req.requested_date}
                                        </Typography>
                                    )}
                                    {req.note && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                            “{req.note}”
                                        </Typography>
                                    )}
                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.75 }}>
                                        <Button size="small" variant="contained" color="primary" onClick={() => openApprove(req)}
                                            sx={{ textTransform: 'none', fontWeight: 700 }}>Approve</Button>
                                        <Button size="small" variant="text" color="error" onClick={() => declineSignup(req)}
                                            sx={{ textTransform: 'none', fontWeight: 700 }}>Decline</Button>
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            )}

            {/* Approve sign-up dialog */}
            <Dialog open={!!approveTarget} onClose={() => setApproveTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Approve — {approveTarget?.customer_name || 'Customer'}</DialogTitle>
                <DialogContent>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        These plans will start together:
                    </Typography>
                    <Box sx={{ mb: 2, p: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                        {(approveTarget?.items || []).map(it => (
                            <Typography key={it.product_id} variant="body2" fontWeight={600}>
                                • {it.product_name} · Qty {it.quantity}
                            </Typography>
                        ))}
                    </Box>
                    <TextField label="Start date" type="date" fullWidth size="small"
                        value={approveForm.date}
                        onChange={e => setApproveForm(f => ({ ...f, date: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        helperText="Applies to every plan above" />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setApproveTarget(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={submitApprove} disabled={!approveForm.date}
                        sx={{ textTransform: 'none', fontWeight: 700 }}>Approve &amp; start</Button>
                </DialogActions>
            </Dialog>

            {/* ── Search + Sort row ───────────────────────────────────────── */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <TextField
                    size="small"
                    placeholder="Search by name or mobile…"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    sx={{
                        flex: '0 0 66%',
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: 'background.paper',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                            border: '1.5px solid',
                            borderColor: searchQuery ? 'primary.main' : 'grey.200',
                            transition: 'border-color 0.2s, box-shadow 0.2s',
                            '& fieldset': { border: 'none' },
                            '&:hover': { borderColor: 'primary.light', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' },
                            '&.Mui-focused': { borderColor: 'primary.main', boxShadow: '0 0 0 3px rgba(37,99,235,0.12)' },
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: searchQuery ? 'primary.main' : 'text.disabled' }} />
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ color: 'text.secondary', p: 0.5 }}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
                <FormControl size="small" sx={{ flexShrink: 0, width: 'calc(34% - 8px)' }}>
                    <Select
                        value={sortBy}
                        onChange={e => setSortBy(e.target.value)}
                        displayEmpty
                        renderValue={(v) => {
                            const labels = {
                                name: 'Name',
                                updated_desc: 'Updated ↓',
                                updated_asc:  'Updated ↑',
                            };
                            return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1 }}>Sort by</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>{labels[v] || v}</Typography>
                                </Box>
                            );
                        }}
                        sx={{
                            borderRadius: 3,
                            bgcolor: 'background.paper',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                            border: '1.5px solid',
                            borderColor: sortBy !== 'name' ? 'primary.main' : 'grey.200',
                            '& fieldset': { border: 'none' },
                            '&:hover': { borderColor: 'primary.light' },
                            '& .MuiSelect-select': { py: '6px' },
                        }}
                    >
                        <MenuItem value="name">Name (A–Z)</MenuItem>
                        <MenuItem value="updated_desc">Last Updated ↓ (Newest)</MenuItem>
                        <MenuItem value="updated_asc">Last Updated ↑ (Oldest)</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* ── Status filter chips ─────────────────────────────────────── */}
            <Box sx={{ mb: 2 }}>
                {/* One line that scrolls sideways rather than wrapping onto a second row */}
                <ScrollChipRow>
                    <Chip
                        label={`All (${customers.length})`}
                        size="small"
                        color={!tagFilter.group ? 'primary' : 'default'}
                        onClick={() => setTagFilter({ group: '', value: '' })}
                        clickable
                    />
                    {STATUS_TAGS.map(tag => {
                        const count = tagCounts[tag] || 0;
                        if (count === 0) return null;
                        const active = tagFilter.group === 'Status' && tagFilter.value === tag;
                        return (
                            <Chip
                                key={tag}
                                label={`${tag} (${count})`}
                                size="small"
                                color={active ? statusTagColor(tag) : 'default'}
                                variant={active ? 'filled' : 'outlined'}
                                onClick={() => setTagFilter(prev =>
                                    prev.group === 'Status' && prev.value === tag
                                        ? { group: '', value: '' }
                                        : { group: 'Status', value: tag }
                                )}
                                clickable
                            />
                        );
                    })}
                </ScrollChipRow>
                <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75 }}>
                    {midMonthCustomerIds.startIds.size > 0 && (
                        <Chip
                            label={`Mid Started (${midMonthCustomerIds.startIds.size})`}
                            size="small"
                            color={midMonthFilter === 'start' ? 'warning' : 'default'}
                            variant={midMonthFilter === 'start' ? 'filled' : 'outlined'}
                            onClick={() => setMidMonthFilter(prev => prev === 'start' ? null : 'start')}
                            clickable
                        />
                    )}
                    {midMonthCustomerIds.endIds.size > 0 && (
                        <Chip
                            label={`Mid Ended (${midMonthCustomerIds.endIds.size})`}
                            size="small"
                            color={midMonthFilter === 'end' ? 'error' : 'default'}
                            variant={midMonthFilter === 'end' ? 'filled' : 'outlined'}
                            onClick={() => setMidMonthFilter(prev => prev === 'end' ? null : 'end')}
                            clickable
                        />
                    )}
                </Box>
            </Box>

            {/* ── Area: one line, busiest two as chips, the rest in a list ── */}
            <AreaFilterRow
                options={sortAreasByRoute(areas)
                    .map(a => ({
                        key: String(a.f_id),
                        label: a.f_name,
                        count: customerGroups.filter(g => g.customer.f_area_id === a.f_id).length,
                    }))
                    .filter(a => a.count > 0)}
                noArea={{ key: NO_AREA_FILTER, count: customerGroups.filter(g => !g.customer.f_area_id).length }}
                total={customerGroups.length}
                value={areaFilter}
                onChange={setAreaFilter}
                sx={{ mt: -1, mb: 2 }}
            />

            {/* ── Customer cards ──────────────────────────────────────────── */}
            {loading ? (
                <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
            ) : filteredGroups.length === 0 ? (
                <Box sx={{ maxWidth: 340, mx: 'auto', textAlign: 'left', py: 3 }}>
                    {customers.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
                                ఇంకా ప్లాన్లు లేవు. ప్రారంభించడానికి కింద చదవండి.<br/>
                                <Typography variant="caption" color="text.disabled">No plans yet. See below to get started.</Typography>
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', p: 1.5, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200', borderRadius: 1.5 }}>
                                <Typography sx={{ fontSize: 20, lineHeight: 1.2, flexShrink: 0 }}>📦</Typography>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        <strong>ప్రోడక్ట్లు జోడించండి</strong> — Settings లో Products కి వెళ్ళి, మీరు అమ్మే వస్తువులు యాడ్ చేయండి (పాలు, న్యూస్‌పేపర్ మొ.).<br/>
                                        <Typography variant="caption" color="text.disabled">Add products first in Settings → Products (e.g. Milk, Newspaper).</Typography>
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', p: 1.5, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200', borderRadius: 1.5 }}>
                                <Typography sx={{ fontSize: 20, lineHeight: 1.2, flexShrink: 0 }}>👤</Typography>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        <strong>కస్టమర్లు జోడించండి</strong> — Customers ట్యాబ్‌లో మీ కస్టమర్లను జోడించండి.<br/>
                                        <Typography variant="caption" color="text.disabled">Add your customers in the Customers tab.</Typography>
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', p: 1.5, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200', borderRadius: 1.5 }}>
                                <Typography sx={{ fontSize: 20, lineHeight: 1.2, flexShrink: 0 }}>✅</Typography>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        <strong>ప్లాన్ అసైన్ చేయండి</strong> — ఇక్కడ ప్రతి కస్టమర్‌కు + నొక్కి, ఏ ప్రోడక్ట్ ఎంత పరిమాణం వెళ్తుందో సెట్ చేయండి.<br/>
                                        <Typography variant="caption" color="text.disabled">Tap + next to each customer here to assign a product &amp; daily quantity.</Typography>
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    ) : (
                        <Typography color="text.secondary" align="center">
                            No customers found.
                        </Typography>
                    )}
                </Box>
            ) : (
                <Stack spacing={1}>
                    {filteredGroups.slice(0, visibleCount).map(({ customer, logs }) => (
                            <CustomerPlanCard
                                key={customer.f_id}
                                businessId={businessId}
                                customer={customer}
                                logs={logs}
                                statusTag={statusTagMap[customer.f_id]}
                                expanded={expandedCustomerId === customer.f_id}
                                onToggleExpand={toggleCardExpandCb}
                                products={products}
                                onChanged={reloadQuietCb}
                            />
                        ))}
                    {filteredGroups.length > visibleCount && (
                        <Box sx={{ textAlign: 'center', mt: 1, mb: 2 }}>
                            <Button variant="outlined" size="small" onClick={() => setVisibleCount(v => v + 50)}>
                                Load more ({filteredGroups.length - visibleCount} remaining)
                            </Button>
                        </Box>
                    )}
                </Stack>
            )}

        </Box>
    );
};

export default CustomerSubscriptionManager;